<template>
	<main class="mcp-viewer">
		<p v-if="state.error" class="message" role="alert">{{ state.error }}</p>
		<p v-else-if="!state.result" class="message" role="status">Loading Swipekit ads…</p>
		<p v-else-if="state.result.isError" class="message" role="alert">Could not load these ads. Check Claude’s tool response, then try again.</p>
		<p v-else-if="!cards.length" class="message" role="status">No ads found. Ask Claude to try another search.</p>
		<template v-else>
			<div class="summary">{{ cards.length }} {{ cards.length === 1 ? 'ad' : 'ads' }} from Swipekit</div>
			<div :key="resultVersion" class="ad-grid" :class="{ single: cards.length === 1 }">
				<AdCard v-for="(card, index) in cards" :key="index" :card="card" :bridge="bridge" />
			</div>
		</template>
	</main>
</template>

<script>
import AdCard from './ad-card.vue';
import { previewCards } from './preview-data.js';

export default {
	components: { AdCard },
	props: ['state', 'config', 'bridge'],
	data: function () { return { resultVersion: 0 }; },
	computed: {
		cards: function () { return previewCards(this.state.result, this.config); },
	},
	watch: {
		'state.result': function () { this.resultVersion += 1; },
	},
};
</script>

<style lang="scss">
.mcp-viewer {
	container-type: inline-size;
	padding: 8px 2px;
	line-height: normal;
	margin: 0;
	background: transparent;
	color: var(--color-text-primary, #262626);
	font-family: var(--font-sans, system-ui, sans-serif);
	font-size: 14px;
	--margin: 8px;
	--margin-lg: 16px;
	--font-size-xs: 12px;
	--font-size-sm: 14px;
	--border-radius: 8px;
	--border-radius-full: 999px;
	--transition-time: 150ms;
	--color-bg-3: var(--color-background-secondary, #eee);
	--color-bg-4: var(--color-background-tertiary, #ddd);
}
.mcp-viewer, .mcp-viewer * { box-sizing: border-box; }
.mcp-viewer {
	.summary { margin-bottom: 12px; color: var(--color-text-secondary, #666); }
	.ad-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: start; }
	.ad-grid.single { grid-template-columns: minmax(0, 1fr); max-width: 600px; margin: 0 auto; }
	.ad-card { min-width: 0; overflow: hidden; background: var(--color-background-primary, #fff); border: 1px solid var(--color-border-primary, #e4e4e4); border-radius: 10px; box-shadow: 0 2px 4px -2px #0000000a, 0 1px 2px #0000000d; }
	.ad-header { display: flex; align-items: center; gap: 12px; padding: 12px; min-height: 64px; }
	.ad-avatar { position: relative; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; background: var(--color-background-secondary, #f0f0f0); color: var(--color-text-secondary, #666); font-size: 17px; font-weight: 500; }
	.ad-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
	.ad-status-dot { position: absolute; bottom: -1px; left: -1px; width: 12px; height: 12px; border: 2px solid var(--color-background-primary, #fff); border-radius: 50%; background: #858585; }
	.ad-status-dot.active { background: #82c71b; }
	.ad-heading { min-width: 0; }
	.ad-card h2 { color: inherit; font-family: inherit; font-weight: 500; font-size: 15px; line-height: 1.25; margin: 0 0 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.platform { margin: 0; color: var(--color-text-secondary, #666); font-size: 12px; line-height: 1.3; }
	.ad-media { background: var(--color-background-secondary, #f3f3f3); }
	.ad-media img, .ad-media video { display: block !important; width: 100% !important; height: auto !important; max-height: 480px; object-fit: contain; }
	.ad-media .VueCarousel-slide { display: flex; align-items: center; min-width: 0; }
	.ad-media .VueCarousel-navigation-button {
		color: #111 !important;
		background: #fff !important;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		margin: 0 !important;
		padding: 0 !important;
		transform: translateY(-50%) !important;
	}
	.ad-media .VueCarousel-navigation-prev { left: 8px; right: auto; }
	.ad-media .VueCarousel-navigation-next { right: 8px; left: auto; }
	.ad-media .VueCarousel-navigation--disabled { visibility: hidden; }
	.ad-media .VueCarousel-dot { background-color: var(--color-text-secondary, #777) !important; }
	.ad-media .VueCarousel-dot--active { background-color: var(--color-text-primary, #111) !important; }
	.ad-copy { padding: 10px; overflow-wrap: anywhere; font-size: 13px; line-height: 1.4; }
	.ad-copy strong { display: block; font-weight: 500; margin-bottom: 2px; }
	.ad-copy p { color: inherit; font: inherit; white-space: pre-wrap; margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
	.ad-copy.expanded p { display: block; }
	.ad-copy button { display: block; font: inherit; color: var(--color-text-info, #4359b8); background: transparent; border: 0; padding: 2px 0; margin-top: 2px; cursor: pointer; }
	.ad-copy button:hover { text-decoration: underline; }
	.ad-details { border-top: 1px solid var(--color-border-primary, #e4e4e4); padding: 10px; background: var(--color-background-secondary, #f7f7f7); font-size: 12px; line-height: 1.5; }
	.ad-labels, .ad-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
	.ad-labels { color: var(--color-text-secondary, #666); text-transform: capitalize; }
	.ad-status { display: inline-flex; align-items: center; gap: 5px; }
	.ad-status:before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #858585; }
	.ad-status.active:before { background: #82c71b; }
	.ad-labels + .ad-stats { margin-top: 8px; }
	.ad-stats { font-variant-numeric: tabular-nums; }
	.ad-stats > span { display: inline-flex; align-items: center; gap: 5px; }
	.ad-stats svg { flex-shrink: 0; color: var(--color-text-secondary, #666); }
	.ad-card footer { padding: 10px; border-top: 1px solid var(--color-border-primary, #e4e4e4); }
	.ad-actions { display: flex; flex-wrap: wrap; gap: 8px; }
	.ad-card footer button { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; font: inherit; font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--color-text-primary, #262626); background: var(--color-background-secondary, #f0f0f0); border: 1px solid var(--color-border-primary, #e4e4e4); border-radius: 6px; padding: 8px 12px; cursor: pointer; }
	.ad-actions button { flex: 1 1 180px; }
	.ad-card footer button:hover { background: var(--color-background-tertiary, #e8e8e8); }
	.ad-card button:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
	.media-fallback, .message { padding: 24px 16px; color: var(--color-text-secondary, #666); line-height: 1.5; }
	.ad-card footer p { margin-bottom: 0; }
	@container (max-width: 560px) { .ad-grid { grid-template-columns: minmax(0, 1fr); } }
	@media (max-width: 560px) { .ad-grid { grid-template-columns: minmax(0, 1fr); } }
}
</style>
